import {
  Blob, BlobType
}                              from '@/models/blob';

/**
 *
 * @class BatchSummaryCreator
 *
 * helper to take a text/data sample (from some file) and tries
 * to guess the file type...based on that content sample.  This can
 * help when the user gives us a .json or a .txt file...which could
 * be any specific type, like a Scenario file or a User Commands
 * file.
 *
 * If we can't guess the type...we don't, we just return none/empty,
 * and the calling logic should do whatever it would have done
 * anyways.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      November 2023
 *
 */
export class ContentAwareTyping {

  /**
   * try to guess the file type based on its content (or at least a sample there of)
   *
   * @param content string the content to use for file type guessing
   *
   */
  public static typeByContent(content: string): string|null {

    /*
     * for scenario (BlobType.Scenario)
     *
     * we expect to see:
     *
     * {
     *    "scenario": {
     *       "links": {
     *           "link": {
     *
     */

    {
      let subject: string = content.replace(/\s+/g, '');

      if (/\{"scenario":\{"links":\{"link"/.test(subject)) {

        return Blob.stringOfType(BlobType.Scenario);
      }
    }

    {
      let subject: string = content.replace(/\s+/g, '');

      if (/\{"scenario":\{"stop_consist_changes":\{/.test(subject)) {

        return Blob.stringOfType(BlobType.Scenario);
      }
    }

    {
      let subject: string = content.replace(/\s+/g, '');

      if (/\{"scenario":\{"conn_time_penalties":\{/.test(subject)) {

        return Blob.stringOfType(BlobType.Scenario);
      }
    }

    {
      let subject: string = content.replace(/\s+/g, '');

      if (/\{"scenario":\{"train_stop_events":\{/.test(subject)) {

        return Blob.stringOfType(BlobType.Scenario);
      }
    }

    {
      let subject: string = content.replace(/\s+/g, '');

      if (/\{"scenario":\{"dps":\{/.test(subject)) {

        return Blob.stringOfType(BlobType.Scenario);
      }
    }

    {
      let subject: string = content.replace(/\s+/g, '');

      if (/\{"scenario":\{"train_stops":\{/.test(subject)) {

        return Blob.stringOfType(BlobType.Scenario);
      }
    }

    {
      let subject: string = content.replace(/\s+/g, '');

      if (/\{"scenario":\{"trains":\{/.test(subject)) {

        return Blob.stringOfType(BlobType.Scenario);
      }
    }

    /*
     * for train movements (simulator output), mainly look for things like "job_detail", that
     * field won't be in any other JSON file we work with.  If we match, we set the type to be
     * BlobType.SimResult.
     *
     */

    {
      let subject: string = content.replace(/\s+/g, '');

      if (/\{"job_detail":\{/.test(subject)) {

        return Blob.stringOfType(BlobType.SimResult);
      }
    }

    {
      let subject: string = content.replace(/\s+/g, '');

      if (/"sim_results":\{/.test(subject)) {

        return Blob.stringOfType(BlobType.SimResult);
      }
    }

    /*
     * for user commands, we are looking for any lines that match
     * known user commands.
     *
     */

    {
      let lines: string[]    = content.split(/\r?\n/);
      let hits: number       = 0;
      let patterns: RegExp[] = [
        /^\s*TRACK:?\s+/,
        /^\s*HOLD:?\s+/,
        /^\s*PARKABILITY:?\s+/,
        /^\s*TRAIN\s+LINKAGE:?\s+/,
        /^\s*MAKE\s+TERMINAL:?\s+WITH\s+WEST\s+DP\s+/,
        /^\s*RESOURCE:?\s+/
      ];

      /* filter out comment lines */

      lines = lines.filter((x: string): boolean => {

        return !/^[#%]/.test(x.trimLeft());
      });

      /* filter out empty lines */

      lines = lines.filter((x: string): boolean => {

        return !(x.length == 0);
      });

      /* check for hits! */

      for (const line of lines) {

        for (const [dummy, pattern] of patterns.entries()) {

          if (pattern.test(line)) {

            hits += 1;
          }
        }

      }

      if (hits == lines.length) {

        /* if all the non-comment lines are user commands, its a user commands file */

        return Blob.stringOfType(BlobType.UserCommands);
      }

      if (hits >= 3) {

        /* we spotted at least 3 user commands, its probably a user commands file */

        return Blob.stringOfType(BlobType.UserCommands);
      }

    }

    /*
     * for diamond crossings, it's a CSV file with a header, so look for
     * recognizable columns in the header.
     *
     *   DP,OCCUPIED,VACATED,START,DURATION,INTERVAL,START_STD,DURATION_STD,SEED
     *   DYER_IL,P000DT01H00M00S,P000DT02H00M00S,,,,,,
     *   DYER_IL,P001DT01H00M00S,P001DT02H00M00S,,,,,,
     *   ...
     *
     */

    {
      let lines: string[]    = content.split(/\r?\n/);

      if (lines.length > 0) {

        let columns: string[]  = lines[0].split(',').map((x: string): string => x.toLowerCase().trim());
        let expected: string[] = [
          'DP',
          'OCCUPIED',
          'VACATED',
          'START',
          'DURATION',
          'INTERVAL',
          'START_STD',
          'DURATION_STD',
          'SEED'
        ].map((x: string): string => x.toLowerCase());

        let required: number = 5;

        let intersection: string[] = columns.filter((x: string): boolean => expected.includes(x));

        if (intersection.length >= required) {

          /* it's a diamond crossings file */

          return Blob.stringOfType(BlobType.DiamondCrossings);

        }
      }
    }

    /* we did not find a match */

    return null
  }
}
